import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { isDarkMode } from "../../utils/constants";
import { getLoadingPhaseMeta } from "./loadingPhaseVisuals";
import { getLoadingPhaseCategory, isInternetSearchPhase } from "./loadingPhaseCatalog";

const ringSpin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`;

const iconSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const haloPulse = keyframes`
  0%   { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
  50%  { opacity: 0.65; transform: translate(-50%, -50%) scale(1.02); }
  100% { opacity: 0.35; transform: translate(-50%, -50%) scale(0.98); }
`;

const nodePulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0.5); }
  70%  { box-shadow: 0 0 0 9px rgba(36, 157, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(36, 157, 255, 0); }
`;

const orbitFloat = keyframes`
  0%   { transform: translate(-50%, -50%) scale(0.98); opacity: 0.72; }
  50%  { transform: translate(-50%, -50%) scale(1.015); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(0.98); opacity: 0.72; }
`;

const orbitFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const CATEGORY_NODES = [
  { key: "tickers", label: "Tickers / watchlist", shortLabel: "Tickers", angle: -90 },
  { key: "pricing", label: "Pricing / charting", shortLabel: "Charts", angle: -45 },
  { key: "stats", label: "Stats refreshed", shortLabel: "Stats", angle: 0 },
  { key: "reports", label: "Reports / filings", shortLabel: "Reports", angle: 45 },
  { key: "criteria", label: "Criteria / options", shortLabel: "Criteria", angle: 90 },
  { key: "markets", label: "Markets scoped", shortLabel: "Markets", angle: 135 },
  { key: "memory", label: "Memory updated", shortLabel: "Memory", angle: 180 },
  { key: "response", label: "Response / exports", shortLabel: "Response", angle: -135 },
];

const STATUS_PENDING = "pending";
const STATUS_ACTIVE = "active";
const STATUS_DONE = "done";

const statusColor = (status, activeColor = "#249DFF") => {
  if (status === STATUS_DONE) return "#32D4A4";
  if (status === STATUS_ACTIVE) return activeColor;
  return "rgba(127, 147, 176, 0.72)";
};

const buildCategoryState = (processQueue = [], phaseText = "") => {
  const initial = CATEGORY_NODES.reduce((acc, node) => {
    acc[node.key] = {
      status: STATUS_PENDING,
      updatedAt: -1,
      lastMessage: "",
      touched: false,
    };
    return acc;
  }, {});

  processQueue.forEach((evt, index) => {
    const payload = evt?.text || {};
    const message = String(payload?.message || "").trim();
    if (!message) return;

    const categoryKey = getLoadingPhaseCategory(message);
    if (!categoryKey || !initial[categoryKey]) return;

    const shouldMarkDone = Boolean(payload?.complete) || payload?.require_completion === false;
    initial[categoryKey] = {
      status: shouldMarkDone ? STATUS_DONE : STATUS_ACTIVE,
      updatedAt: index,
      lastMessage: message,
      touched: true,
    };
  });

  const activeKeys = Object.entries(initial)
    .filter(([, value]) => value.status === STATUS_ACTIVE)
    .sort((a, b) => b[1].updatedAt - a[1].updatedAt)
    .map(([key]) => key);

  if (activeKeys.length > 1) {
    activeKeys.slice(1).forEach((key) => {
      initial[key] = {
        ...initial[key],
        status: STATUS_DONE,
      };
    });
  }

  const phaseCategoryKey = getLoadingPhaseCategory(phaseText);
  const hasActiveNode = Object.values(initial).some((value) => value.status === STATUS_ACTIVE);
  if (!hasActiveNode && phaseCategoryKey && initial[phaseCategoryKey]) {
    initial[phaseCategoryKey] = {
      ...initial[phaseCategoryKey],
      status: STATUS_ACTIVE,
      updatedAt: processQueue.length,
      lastMessage: String(phaseText || "").trim(),
      touched: true,
    };
  }

  const hasAnyTouched = Object.values(initial).some((value) => value.touched);
  if (!hasAnyTouched) {
    const fallbackKey = phaseCategoryKey && initial[phaseCategoryKey] ? phaseCategoryKey : "response";
    initial[fallbackKey] = {
      ...initial[fallbackKey],
      status: STATUS_ACTIVE,
      lastMessage: String(phaseText || "").trim(),
    };
  }

  return initial;
};

const LoadingPhasesOrbit = ({ phaseText, phaseDetailText, processQueue }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const phaseMeta = useMemo(() => getLoadingPhaseMeta(phaseText, isDarkMode), [phaseText]);
  const categoryState = useMemo(() => buildCategoryState(processQueue, phaseText), [processQueue, phaseText]);
  const isSearchPhase = isInternetSearchPhase(phaseText);
  const detailText = String(phaseDetailText || "").trim();

  const activeNode = useMemo(() => {
    return CATEGORY_NODES.find((node) => categoryState[node.key]?.status === STATUS_ACTIVE) || null;
  }, [categoryState]);

  const activeMessage = useMemo(() => {
    if (isSearchPhase) {
      return String(phaseText || "").trim() || phaseMeta.detail;
    }
    if (activeNode && categoryState[activeNode.key]?.lastMessage) {
      return categoryState[activeNode.key].lastMessage;
    }
    return String(phaseText || "").trim() || phaseMeta.detail;
  }, [activeNode, categoryState, isSearchPhase, phaseMeta.detail, phaseText]);

  const canvasSize = isMobile ? 286 : 520;
  const orbitRadius = isMobile ? 82 : 156;
  const ringSize = isMobile ? 162 : 278;
  const lineLength = isMobile ? 22 : 40;
  const labelRadius = orbitRadius + lineLength + (isMobile ? 8 : 18);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: isMobile ? 312 : 460,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        py: isMobile ? 1 : 1.5,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: canvasSize,
          height: canvasSize,
          maxWidth: "100%",
          maxHeight: "100%",
          animation: `${orbitFadeIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
          willChange: "opacity, transform",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: ringSize + 56,
            height: ringSize + 56,
            borderRadius: "50%",
            transform: "translate(-50%, -50%)",
            background: phaseMeta.haloBackground,
            animation: `${haloPulse} 2.4s ease-in-out infinite`,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: ringSize + 28,
            height: ringSize + 28,
            borderRadius: "50%",
            border: `1px dashed ${phaseMeta.dashedBorder}`,
            transform: "translate(-50%, -50%)",
            animation: `${ringSpin} 24s linear infinite`,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: ringSize,
            height: ringSize,
            borderRadius: "50%",
            border: `2px solid ${phaseMeta.ringBorder}`,
            boxShadow: phaseMeta.ringGlow,
            transform: "translate(-50%, -50%)",
          }}
        />

        {isSearchPhase &&
          [0, 1, 2].map((idx) => (
            <Box
              key={`search-orbit-${idx}`}
              sx={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: ringSize - idx * 26,
                height: ringSize - idx * 26,
                transform: "translate(-50%, -50%)",
                animation: `${ringSpin} ${10 + idx * 3}s linear infinite`,
                animationDirection: idx % 2 === 0 ? "normal" : "reverse",
                opacity: 0.8 - idx * 0.18,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: -4,
                  width: idx === 0 ? 9 : 7,
                  height: idx === 0 ? 9 : 7,
                  borderRadius: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: idx === 0 ? phaseMeta.accent : phaseMeta.secondaryAccent,
                  boxShadow: `0 0 16px ${idx === 0 ? "rgba(34, 211, 166, 0.42)" : "rgba(124, 240, 206, 0.3)"}`,
                }}
              />
            </Box>
          ))}

        {CATEGORY_NODES.map((node) => {
          const status = categoryState[node.key]?.status || STATUS_PENDING;
          const color = statusColor(status, phaseMeta.accent);
          const radians = (node.angle * Math.PI) / 180;

          const nodeX = Math.cos(radians) * orbitRadius;
          const nodeY = Math.sin(radians) * orbitRadius;
          const lineX = Math.cos(radians) * (orbitRadius + lineLength / 2);
          const lineY = Math.sin(radians) * (orbitRadius + lineLength / 2);
          const labelX = Math.cos(radians) * labelRadius;
          const labelY = Math.sin(radians) * labelRadius;
          const xCos = Math.cos(radians);

          const labelAnchor = xCos > 0.35 ? "left" : xCos < -0.35 ? "right" : "center";
          const labelTransform =
            labelAnchor === "left"
              ? "translate(0%, -50%)"
              : labelAnchor === "right"
                ? "translate(-100%, -50%)"
                : "translate(-50%, -50%)";

          return (
            <React.Fragment key={node.key}>
              <Box
                sx={{
                  position: "absolute",
                  left: `calc(50% + ${lineX - lineLength / 2}px)`,
                  top: `calc(50% + ${lineY}px)`,
                  width: lineLength,
                  height: "1.5px",
                  backgroundColor: color,
                  opacity: status === STATUS_PENDING ? 0.42 : 0.95,
                  transform: `translateY(-50%) rotate(${node.angle}deg)`,
                  transformOrigin: "center",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: `calc(50% + ${nodeX}px)`,
                  top: `calc(50% + ${nodeY}px)`,
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: color,
                  border: `2px solid ${isDarkMode ? "#0f1520" : "#ffffff"}`,
                  transform: "translate(-50%, -50%)",
                  animation: status === STATUS_ACTIVE ? `${nodePulse} 1.6s ease-out infinite` : "none",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: `calc(50% + ${labelX}px)`,
                  top: `calc(50% + ${labelY}px)`,
                  transform: labelTransform,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: isMobile ? 0.35 : 0.5,
                  px: isMobile ? 0.6 : 0.8,
                  py: isMobile ? 0.28 : 0.35,
                  borderRadius: "10px",
                  border: `1px solid ${phaseMeta.labelBorder}`,
                  backgroundColor: phaseMeta.labelBackground,
                  color: phaseMeta.labelColor,
                  maxWidth: isMobile ? 96 : 164,
                }}
              >
                {status === STATUS_DONE ? (
                  <CheckRoundedIcon sx={{ fontSize: isMobile ? 11 : 13, color }} />
                ) : status === STATUS_ACTIVE ? (
                  <AutorenewRounded
                    sx={{
                      fontSize: isMobile ? 10 : 12,
                      color,
                      animation: `${iconSpin} 1.2s linear infinite`,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: isMobile ? 6 : 7,
                      height: isMobile ? 6 : 7,
                      borderRadius: "50%",
                      backgroundColor: color,
                    }}
                  />
                )}
                <Typography
                  sx={{
                    fontSize: isMobile ? "9px" : "11px",
                    fontWeight: 600,
                    lineHeight: isMobile ? 1.2 : 1.25,
                    letterSpacing: isMobile ? 0 : 0.1,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {isMobile ? node.shortLabel || node.label : node.label}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? 150 : 232,
            minHeight: isMobile ? 150 : 232,
            borderRadius: "50%",
            border: `1px solid ${phaseMeta.centerBorder}`,
            background: phaseMeta.centerBackground,
            boxShadow: isDarkMode
              ? "0 18px 42px rgba(0,0,0,0.48)"
              : "0 16px 40px rgba(27, 97, 191, 0.22)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: isMobile ? 1.5 : 2.2,
            py: isMobile ? 1.6 : 2.1,
            animation: isSearchPhase ? `${orbitFloat} 2.8s ease-in-out infinite` : "none",
          }}
        >
          <Box
            sx={{
              width: isMobile ? 34 : 42,
              height: isMobile ? 34 : 42,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 0.9,
              bgcolor: isSearchPhase
                ? (isDarkMode ? "rgba(34,211,166,0.18)" : "rgba(34,211,166,0.14)")
                : (isDarkMode ? "rgba(36,157,255,0.16)" : "rgba(36,157,255,0.12)"),
              color: phaseMeta.accent,
            }}
          >
            {isSearchPhase ? (
              <PublicRoundedIcon sx={{ fontSize: isMobile ? 18 : 22 }} />
            ) : (
              <AutorenewRounded
                sx={{
                  fontSize: isMobile ? 17 : 20,
                  animation: `${iconSpin} 2s linear infinite`,
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: isMobile ? "10px" : "11px",
              letterSpacing: 1.25,
              textTransform: "uppercase",
              color: phaseMeta.headingColor,
              fontWeight: 700,
              mb: 0.7,
            }}
          >
            {phaseMeta.eyebrow}
          </Typography>
          <Typography
            sx={{
              fontSize: isMobile ? "12px" : "14px",
              lineHeight: 1.35,
              color: phaseMeta.titleColor,
              fontWeight: 650,
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            {phaseText}
          </Typography>
          <Typography
            sx={{
              mt: 0.85,
              fontSize: isMobile ? "9px" : "10px",
              lineHeight: 1.35,
              color: phaseMeta.bodyColor,
              maxWidth: isMobile ? 122 : 190,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: isSearchPhase ? "normal" : "nowrap",
            }}
          >
            {isSearchPhase ? phaseMeta.description : activeMessage}
          </Typography>
          {isSearchPhase && (
            <Typography
              sx={{
                mt: 0.8,
                px: 0.9,
                py: 0.45,
                borderRadius: "999px",
                fontSize: isMobile ? "8px" : "9px",
                fontWeight: 700,
                letterSpacing: 0.25,
                color: phaseMeta.labelColor,
                bgcolor: isDarkMode ? "rgba(34,211,166,0.12)" : "rgba(34,211,166,0.1)",
                maxWidth: isMobile ? 128 : 170,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeMessage}
            </Typography>
          )}
          {detailText && (
            <Box
              sx={{
                mt: 1.05,
                px: 1.0,
                py: 0.9,
                maxWidth: isMobile ? 150 : 196,
                borderRadius: 2,
                bgcolor: isDarkMode ? "rgba(9,16,28,0.48)" : "rgba(255,255,255,0.6)",
                border: `1px solid ${phaseMeta.panelBorder}`,
                boxShadow: isDarkMode
                  ? "0 8px 18px rgba(0,0,0,0.22)"
                  : "0 8px 16px rgba(18,72,145,0.08)",
              }}
            >
              <Typography
                sx={{
                  fontSize: isMobile ? "8px" : "9px",
                  fontWeight: 700,
                  letterSpacing: 0.55,
                  textTransform: "uppercase",
                  color: phaseMeta.headingColor,
                  mb: 0.45,
                }}
              >
                Thinking Snapshot
              </Typography>
              <Typography
                sx={{
                  fontSize: isMobile ? "8.5px" : "9.5px",
                  lineHeight: 1.45,
                  color: phaseMeta.panelTextColor,
                  whiteSpace: "normal",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: isMobile ? 4 : 5,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {detailText}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingPhasesOrbit;
